// KRW + percentage formatters. Keep all number → string conversion here so
// the dashboard speaks one consistent dialect of Korean trading numbers.

const krwFormatter = new Intl.NumberFormat('ko-KR');

export function krw(n: number | null | undefined, opts?: { sign?: boolean }): string {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = opts?.sign && n > 0 ? '+' : '';
  return `${sign}₩${krwFormatter.format(Math.round(n))}`;
}

/** Compact KRW for tight headers: ₩98.4M, ₩342k. */
export function krwShort(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_0000_0000) return `₩${(n / 1_0000_0000).toFixed(2)}억`;        // 1억 = 100M
  if (abs >= 1_000_0000) return `₩${(n / 1_000_0000).toFixed(2)}천만`;        // 1천만 = 10M
  if (abs >= 1_0000) return `₩${(n / 1_0000).toFixed(1)}만`;                  // 1만 = 10k
  return krw(n);
}

/** Plain integer with thousands separators, no currency symbol. */
export function num(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return krwFormatter.format(Math.round(n));
}

/** Signed percentage: +1.69%, -2.05%. Always shows 2 decimals. */
export function pct(n: number | null | undefined, opts?: { sign?: boolean }): string {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = opts?.sign !== false && n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

/** Tailwind class for a P&L number: green if positive, red if negative. */
export function pnlColor(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n) || n === 0) return 'text-text-dim';
  return n > 0 ? 'text-kam-green' : 'text-kam-red';
}

/** HH:mm:ss localized. */
export function timeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour12: false });
}

/** "5m ago", "2h ago", "Jun 3". */
export function ago(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
